import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Router as RouterIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Sync as SyncIcon,
  VpnKey as VpnKeyIcon,
  ContentCopy as CopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Memory as MemoryIcon
} from '@mui/icons-material';
import axios from 'axios';

const EdgeGatewayManagement = () => {
  const [gateways, setGateways] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [gatewayToDelete, setGatewayToDelete] = useState(null);
  const [apiKeyDialog, setApiKeyDialog] = useState({ open: false, apiKey: '', gatewayId: '', gatewayName: '' });
  const [showApiKey, setShowApiKey] = useState(false);
  const [assignMachineDialog, setAssignMachineDialog] = useState({ open: false, gateway: null });
  const [selectedMachineId, setSelectedMachineId] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    polling_interval_seconds: 30,
    sync_batch_size: 100
  });

  const loadGateways = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/api/edge-gateways');
      setGateways(response.data);
    } catch (err) {
      console.error('Error loading edge gateways:', err);
      setError('Errore nel caricamento degli Edge Gateway');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMachines = useCallback(async () => {
    try {
      const response = await axios.get('/api/machines');
      setMachines(response.data);
    } catch (err) {
      console.error('Error loading machines:', err);
    }
  }, []);

  useEffect(() => {
    loadGateways();
    loadMachines();
  }, [loadGateways, loadMachines]);

  const handleAdd = () => {
    setSelectedGateway(null);
    setFormData({
      name: '',
      description: '',
      polling_interval_seconds: 30,
      sync_batch_size: 100
    });
    setOpenDialog(true);
  };

  const handleEdit = (gateway) => {
    setSelectedGateway(gateway);
    setFormData({
      name: gateway.name,
      description: gateway.description || '',
      polling_interval_seconds: gateway.polling_interval_seconds,
      sync_batch_size: gateway.sync_batch_size
    });
    setOpenDialog(true);
  };

  const handleDelete = (gateway) => {
    setGatewayToDelete(gateway);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`/api/edge-gateways/${gatewayToDelete.id}`);
      setDeleteConfirmOpen(false);
      setGatewayToDelete(null);
      loadGateways();
      setSnackbar({ open: true, message: 'Gateway eliminato con successo', severity: 'success' });
    } catch (err) {
      console.error('Error deleting gateway:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Errore nell\'eliminazione del gateway',
        severity: 'error'
      });
    }
  };

  const handleSave = async () => {
    try {
      if (selectedGateway) {
        await axios.put(`/api/edge-gateways/${selectedGateway.id}`, formData);
        setSnackbar({ open: true, message: 'Gateway aggiornato con successo', severity: 'success' });
      } else {
        const response = await axios.post('/api/edge-gateways', formData);
        // Show API key dialog with gateway ID
        setApiKeyDialog({
          open: true,
          apiKey: response.data.api_key,
          gatewayId: response.data.id,
          gatewayName: formData.name
        });
      }
      setOpenDialog(false);
      loadGateways();
    } catch (err) {
      console.error('Error saving gateway:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Errore nel salvataggio del gateway',
        severity: 'error'
      });
    }
  };

  const handleRegenerateKey = async (gateway) => {
    if (!window.confirm('Sei sicuro di voler rigenerare la chiave API? La chiave attuale non funzionerà più.')) {
      return;
    }

    try {
      const response = await axios.post(`/api/edge-gateways/${gateway.id}/regenerate-key`);
      setApiKeyDialog({
        open: true,
        apiKey: response.data.api_key,
        gatewayId: gateway.id,
        gatewayName: gateway.name
      });
    } catch (err) {
      console.error('Error regenerating key:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Errore nella rigenerazione della chiave',
        severity: 'error'
      });
    }
  };

  const handleAssignMachine = (gateway) => {
    setAssignMachineDialog({ open: true, gateway });
    setSelectedMachineId('');
  };

  const confirmAssignMachine = async () => {
    try {
      await axios.post(`/api/edge-gateways/${assignMachineDialog.gateway.id}/assign-machine`, {
        machine_id: selectedMachineId
      });
      setAssignMachineDialog({ open: false, gateway: null });
      loadGateways();
      loadMachines();
      setSnackbar({ open: true, message: 'Macchina assegnata con successo', severity: 'success' });
    } catch (err) {
      console.error('Error assigning machine:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Errore nell\'assegnazione della macchina',
        severity: 'error'
      });
    }
  };

  const handleUnassignMachine = async (gatewayId, machineId) => {
    try {
      await axios.post(`/api/edge-gateways/${gatewayId}/unassign-machine`, {
        machine_id: machineId
      });
      loadGateways();
      loadMachines();
      setSnackbar({ open: true, message: 'Macchina rimossa dal gateway', severity: 'success' });
    } catch (err) {
      console.error('Error unassigning machine:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Errore nella rimozione della macchina',
        severity: 'error'
      });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'Chiave copiata negli appunti', severity: 'info' });
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      active: { color: 'success', icon: <CheckCircleIcon />, label: 'Attivo' },
      pending: { color: 'warning', icon: <ScheduleIcon />, label: 'In attesa' },
      offline: { color: 'default', icon: <ErrorIcon />, label: 'Offline' },
      error: { color: 'error', icon: <WarningIcon />, label: 'Errore' }
    };

    const config = statusConfig[status] || statusConfig.offline;

    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
      />
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Mai';
    return new Date(dateString).toLocaleString('it-IT');
  };

  // Filter unassigned machines
  const unassignedMachines = machines.filter(m => !m.edge_gateway_uuid);

  if (loading && gateways.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <RouterIcon color="primary" fontSize="large" />
            <Typography variant="h4" component="h1">
              Edge Gateway
            </Typography>
          </Box>
          <Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadGateways}
              sx={{ mr: 2 }}
            >
              Aggiorna
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
            >
              Nuovo Gateway
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {gateways.map((gateway) => (
            <Grid item xs={12} md={6} lg={4} key={gateway.id}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6" component="div">
                      {gateway.name}
                    </Typography>
                    {getStatusChip(gateway.status)}
                  </Box>

                  {gateway.description && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {gateway.description}
                    </Typography>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <List dense>
                    <ListItem disablePadding>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <SyncIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Ultimo Heartbeat"
                        secondary={formatDate(gateway.last_heartbeat)}
                      />
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <ScheduleIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Intervallo Polling"
                        secondary={`${gateway.polling_interval_seconds} secondi`}
                      />
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <MemoryIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Macchine Assegnate"
                        secondary={`${gateway.assigned_machines || 0} (${gateway.active_machines || 0} attive)`}
                      />
                    </ListItem>
                  </List>

                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      ID: {gateway.id}
                      <IconButton size="small" onClick={() => copyToClipboard(gateway.id)} sx={{ ml: 0.5, p: 0.25 }}>
                        <CopyIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Typography>
                    {gateway.ip_address && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        IP: {gateway.ip_address}
                      </Typography>
                    )}
                  </Box>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Box>
                    <Tooltip title="Modifica">
                      <IconButton size="small" onClick={() => handleEdit(gateway)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Rigenera Chiave API">
                      <IconButton size="small" onClick={() => handleRegenerateKey(gateway)}>
                        <VpnKeyIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Elimina">
                      <IconButton size="small" color="error" onClick={() => handleDelete(gateway)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleAssignMachine(gateway)}
                    disabled={unassignedMachines.length === 0}
                  >
                    Assegna Macchina
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}

          {gateways.length === 0 && !loading && (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <RouterIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Nessun Edge Gateway configurato
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Crea un nuovo Edge Gateway per collegare le macchine alla dashboard
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
                  Crea Gateway
                </Button>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedGateway ? 'Modifica Edge Gateway' : 'Nuovo Edge Gateway'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nome Gateway"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Descrizione"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            label="Intervallo Polling (secondi)"
            type="number"
            value={formData.polling_interval_seconds}
            onChange={(e) => setFormData({ ...formData, polling_interval_seconds: parseInt(e.target.value) })}
            margin="normal"
            inputProps={{ min: 5, max: 300 }}
            helperText="Frequenza di sincronizzazione con il cloud (5-300 secondi)"
          />
          <TextField
            fullWidth
            label="Batch Size Sync"
            type="number"
            value={formData.sync_batch_size}
            onChange={(e) => setFormData({ ...formData, sync_batch_size: parseInt(e.target.value) })}
            margin="normal"
            inputProps={{ min: 10, max: 1000 }}
            helperText="Numero massimo di eventi per sincronizzazione"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Annulla</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formData.name}>
            {selectedGateway ? 'Salva' : 'Crea'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* API Key Dialog */}
      <Dialog open={apiKeyDialog.open} onClose={() => setApiKeyDialog({ open: false, apiKey: '', gatewayId: '', gatewayName: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Credenziali per {apiKeyDialog.gatewayName}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Queste credenziali vengono mostrate solo una volta. Copiale e conservale in modo sicuro.
          </Alert>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Gateway ID:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
            <TextField
              fullWidth
              value={apiKeyDialog.gatewayId}
              InputProps={{ readOnly: true }}
              size="small"
            />
            <IconButton onClick={() => copyToClipboard(apiKeyDialog.gatewayId)}>
              <CopyIcon />
            </IconButton>
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            API Key:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
            <TextField
              fullWidth
              value={apiKeyDialog.apiKey}
              type={showApiKey ? 'text' : 'password'}
              InputProps={{ readOnly: true }}
              size="small"
            />
            <IconButton onClick={() => setShowApiKey(!showApiKey)}>
              {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
            <IconButton onClick={() => copyToClipboard(apiKeyDialog.apiKey)}>
              <CopyIcon />
            </IconButton>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Aggiungi queste variabili al file <strong>.env</strong> dell'Edge Gateway:
          </Typography>
          <Box sx={{ bgcolor: 'grey.900', color: 'grey.100', p: 2, borderRadius: 1, mt: 1, fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
{`EDGE_GATEWAY_ID=${apiKeyDialog.gatewayId}
EDGE_API_KEY=${showApiKey ? apiKeyDialog.apiKey : '***'}`}
          </Box>
          <Button
            size="small"
            startIcon={<CopyIcon />}
            onClick={() => copyToClipboard(`EDGE_GATEWAY_ID=${apiKeyDialog.gatewayId}\nEDGE_API_KEY=${apiKeyDialog.apiKey}`)}
            sx={{ mt: 1 }}
          >
            Copia entrambe
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApiKeyDialog({ open: false, apiKey: '', gatewayId: '', gatewayName: '' })} variant="contained">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Machine Dialog */}
      <Dialog open={assignMachineDialog.open} onClose={() => setAssignMachineDialog({ open: false, gateway: null })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Assegna Macchina a {assignMachineDialog.gateway?.name}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Seleziona Macchina</InputLabel>
            <Select
              value={selectedMachineId}
              onChange={(e) => setSelectedMachineId(e.target.value)}
              label="Seleziona Macchina"
            >
              {unassignedMachines.map((machine) => (
                <MenuItem key={machine.id} value={machine.id}>
                  {machine.name} ({machine.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {unassignedMachines.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Tutte le macchine sono già assegnate a un gateway.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignMachineDialog({ open: false, gateway: null })}>Annulla</Button>
          <Button onClick={confirmAssignMachine} variant="contained" disabled={!selectedMachineId}>
            Assegna
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler eliminare il gateway "{gatewayToDelete?.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Questa azione non può essere annullata.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Annulla</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EdgeGatewayManagement;
